<template>
  <div class="variable-selector" :class="{ 'dropdown-open': isOpen }">
    <multiselect
      v-model="internalValue"
      :options="variableOptions"
      :searchable="true"
      :close-on-select="!multiple"
      :taggable="allowCustom"
      @tag="onCustomTag"
      tag-placeholder="Créer cette variable"
      :multiple="multiple"
      :show-labels="false"
      open-direction="bottom"
      :placeholder="placeholder"
      label="slug"
      track-by="id"
      @select="onSelect"
      @remove="onRemove"
      @open="onOpen"
      @close="onClose"
      class="variable-multiselect"
    >
      <template #option="{ option }">
        <div class="peelo-option">
          <div class="peelo-option-icon" v-if="!option.$isDisabled">
            <svg v-if="option.isSpecial && option.id === 'EMPTY'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
            <svg v-else-if="option.isCustom" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="16 18 22 12 16 6"></polyline>
              <polyline points="8 6 2 12 8 18"></polyline>
            </svg>
          </div>
          <div class="peelo-option-content">
            <span v-if="option.isSpecial" class="option-special">{{ option.slug }}</span>
            <span v-else-if="option.$isDisabled" class="option-separator">{{ option.slug }}</span>
            <span v-else class="option-variable">
              <span class="var-slug">{{ option.slug }}</span>
              <span class="var-id">({{ option.id }})</span>
            </span>
          </div>
        </div>
      </template>
      <template #singleLabel="{ option }">
        <div class="peelo-single-action-chip" v-if="!option.isSpecial">
          <div class="peelo-single-icon">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="16 18 22 12 16 6"></polyline>
              <polyline points="8 6 2 12 8 18"></polyline>
            </svg>
          </div>
          <div class="peelo-single-text">
            <span class="action-title">{{ option.slug }}</span>
          </div>
        </div>
        <div class="peelo-single-label" v-else>
          <span>{{ option.slug }}</span>
        </div>
      </template>
      <template #selection="{ values, search, isOpen }">
        <span class="multiselect__single" v-if="multiple && values.length && !isOpen">
          {{ values.length }} variable(s) sélectionnée(s)
        </span>
      </template>
      <template #caret="{ toggle }">
        <div class="peelo-caret" @mousedown.prevent.stop="toggle">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      </template>
      <template #noResult>
        <div class="peelo-empty-state">
          <div class="empty-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <div class="empty-text">
            <h4>Aucune variable trouvée</h4>
            <p>Essayez de modifier votre recherche.</p>
          </div>
        </div>
      </template>
      <template #noOptions>
        <div class="peelo-empty-state">
          <div class="empty-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="9" x2="15" y2="15"></line>
              <line x1="15" y1="9" x2="9" y2="15"></line>
            </svg>
          </div>
          <div class="empty-text">
            <h4>La liste est vide</h4>
            <p>Il n'y a aucune variable disponible à sélectionner.</p>
          </div>
        </div>
      </template>
    </multiselect>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

export default {
  name: 'VariableSelector',
  components: {
    Multiselect
  },
  props: {
    // Vue 2 (value) / Vue 3 (modelValue)
    value: {
      type: [String, Object, Array],
      default: ''
    },
    modelValue: {
      type: [String, Object, Array],
      default: ''
    },
    variables: {
      type: Array,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Sélectionner une variable'
    },
    allowEmpty: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    allowCustom: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      internalValue: this.multiple ? [] : null
    };
  },
  computed: {
    variableOptions() {
      const options = [];
      
      if (this.allowEmpty && !this.multiple) {
        options.push({
          id: 'EMPTY',
          slug: '-- Aucune variable --',
          isSpecial: true
        });
        
        options.push({
          id: 'SEPARATOR',
          slug: '──────────────────',
          $isDisabled: true
        });
      }
      
      if (this.variables && this.variables.length > 0) {
        // Only include variables that have an id and slug
        options.push(...this.variables.filter(v => v && v.id && v.slug));
      }
      
      return options;
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.syncInternalValue(newVal);
      }
    },
    modelValue: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.syncInternalValue(newVal);
      }
    },
    variables: {
      deep: true,
      handler() {
        this.syncInternalValue(this.modelValue || this.value);
      }
    }
  },
  methods: {
    syncInternalValue(val) {
      if (!val || (Array.isArray(val) && val.length === 0)) {
        if (this.allowEmpty && !this.multiple) {
          this.internalValue = this.variableOptions.find(o => o.id === 'EMPTY') || null;
        } else {
          this.internalValue = this.multiple ? [] : null;
        }
        return;
      }

      if (this.multiple) {
        const valArray = Array.isArray(val) ? val : [val];
        this.internalValue = valArray.map(v => {
          if (typeof v === 'object' && v.id) return v;
          const found = this.variables.find(vari => vari.id === v);
          return found || { id: v, slug: v };
        });
        return;
      }
      
      // Si la valeur est déjà un objet avec un id
      if (typeof val === 'object' && val.id) {
        this.internalValue = val;
        return;
      }

      // Si la valeur est une string (id)
      if (typeof val === 'string') {
        const found = this.variables.find(v => v.id === val);
        if (found) {
          this.internalValue = found;
        } else {
          this.internalValue = { id: val, slug: val };
        }
      }
    },
    onSelect(selectedOption) {
      if (!selectedOption) return;
      this.emitChange();
    },
    onCustomTag(newTag) {
      const tag = {
        id: newTag,
        slug: newTag,
        isCustom: true
      };
      // We don't add it to this.variables because it's a prop.
      // Instead, we just set it as internalValue.
      if (this.multiple) {
        this.internalValue.push(tag);
      } else {
        this.internalValue = tag;
      }
      this.emitChange();
    },
    onRemove() {
      this.emitChange();
    },
    emitChange() {
      this.$nextTick(() => {
        let emitVal;
        
        if (this.multiple) {
          if (!this.internalValue) emitVal = [];
          else emitVal = this.returnObject ? this.internalValue : this.internalValue.map(v => v.id);
        } else {
          if (!this.internalValue || this.internalValue.id === 'EMPTY') {
            emitVal = '';
          } else {
            emitVal = this.returnObject ? this.internalValue : this.internalValue.id;
          }
        }
        
        this.$emit('input', emitVal);
        this.$emit('update:modelValue', emitVal);
        this.$emit('change', emitVal);
      });
    },
    onOpen() {
      this.isOpen = true;
    },
    onClose() {
      this.isOpen = false;
    }
  }
}
</script>

<style scoped>
/* Design inspiré par BlockSelector.vue avec l'esthétique Peelo */
.variable-selector {
  position: relative;
  width: 100%;
}

::v-deep(.variable-multiselect) {
  min-height: 44px;
}

::v-deep(.variable-multiselect .multiselect__tags) {
  min-height: 44px;
  padding: 8px 40px 8px 12px;
  border-radius: 12px;
  border: 1px solid rgba(20, 48, 40, 0.1);
  background: #ffffff;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 5px rgba(20, 48, 40, 0.02);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

::v-deep(.variable-multiselect.multiselect--active .multiselect__tags) {
  border-color: #143028; /* Peelo Dark Green */
  box-shadow: 0 0 0 4px rgba(218, 255, 209, 0.5); /* Peelo Mint glow */
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

::v-deep(.variable-multiselect .multiselect__placeholder) {
  color: #94a3b8;
  margin: 0;
  padding: 0;
  font-size: 0.95rem;
}

::v-deep(.variable-multiselect .multiselect__content-wrapper) {
  border: 1px solid rgba(20, 48, 40, 0.15);
  border-top: none;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  box-shadow: 0 15px 35px rgba(20, 48, 40, 0.15), 0 5px 15px rgba(0, 0, 0, 0.05);
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  margin-top: -1px;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 50;
}

::v-deep(.variable-multiselect .multiselect__content) {
  padding: 6px;
}

::v-deep(.variable-multiselect .multiselect__option) {
  padding: 0;
  min-height: 40px;
  border-radius: 8px;
  margin-bottom: 4px;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease;
}

::v-deep(.variable-multiselect .multiselect__option--highlight) {
  background: rgba(218, 255, 209, 0.5); /* Mint transparent */
  color: #143028;
  transform: translateX(4px);
}

::v-deep(.variable-multiselect .multiselect__option--selected) {
  background: #143028;
  color: #daffd1;
  font-weight: 600;
}

::v-deep(.variable-multiselect .multiselect__option--selected.multiselect__option--highlight) {
  background: #1a4035;
  color: #ffffff;
  transform: translateX(4px);
}

.peelo-caret {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  pointer-events: auto;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-open .peelo-caret {
  transform: translateY(-50%) rotate(180deg);
  color: #143028;
}

.peelo-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  width: 100%;
}

.peelo-option-icon {
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  opacity: 0.7;
}

.peelo-option-content {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

.option-special {
  font-style: italic;
  opacity: 0.8;
}

.option-separator {
  color: #cbd5e1;
  text-align: center;
  width: 100%;
  letter-spacing: 2px;
}

.option-variable {
  display: flex;
  flex-direction: column;
}

.var-slug {
  font-weight: 600;
  font-size: 0.95rem;
}

.var-id {
  font-size: 0.75rem;
  opacity: 0.7;
  font-family: monospace;
}

::v-deep(.multiselect__option--selected) .var-id {
  opacity: 0.9;
  color: #daffd1;
}

.peelo-single-label {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #0f172a;
}

/* Empty State / No Results */
.peelo-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  text-align: center;
  background: #f8fafc;
  border-radius: 8px;
  margin: 4px;
}

.empty-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  margin-bottom: 12px;
}

.empty-text h4 {
  margin: 0 0 4px 0;
  color: #334155;
  font-size: 0.95rem;
  font-weight: 600;
}

.empty-text p {
  margin: 0;
  color: #64748b;
  font-size: 0.85rem;
}

/* Multiple Selection Tags */
::v-deep(.variable-multiselect .multiselect__single) {
  background: transparent;
  font-size: 14.5px;
  color: #143028;
  font-weight: 500;
  margin-bottom: 0;
  padding-left: 0;
  line-height: 22px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-top: 2px;
}

.peelo-single-action-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(218, 255, 209, 0.5);
  border: 1px solid rgba(20, 48, 40, 0.1);
  padding: 4px 12px 4px 4px;
  border-radius: 20px;
  transform: translateY(-4px);
}

.peelo-single-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #143028;
  color: #DAFFD1;
  border-radius: 50%;
}

.peelo-single-text {
  display: flex;
  align-items: center;
  gap: 6px;
}

.peelo-single-text .action-title {
  font-weight: 600;
  color: #143028;
  font-size: 13.5px;
}

.peelo-single-text .action-subtitle {
  color: #64748b;
  font-size: 11.5px;
  opacity: 0.8;
}

::v-deep(.variable-multiselect .multiselect__tag) {
  background: #143028;
  color: #daffd1;
  border-radius: 6px;
  padding: 4px 24px 4px 8px;
  margin-bottom: 4px;
  margin-right: 4px;
  font-weight: 500;
  font-size: 0.85rem;
}

::v-deep(.variable-multiselect .multiselect__tag-icon) {
  line-height: 24px;
}

::v-deep(.variable-multiselect .multiselect__tag-icon:after) {
  color: #daffd1;
}

::v-deep(.variable-multiselect .multiselect__tag-icon:focus),
::v-deep(.variable-multiselect .multiselect__tag-icon:hover) {
  background: rgba(218, 255, 209, 0.2);
}
</style>
